import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Search, Atom, Sparkles, Sun, HelpCircle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const categories = [
    { id: 'all', name: 'All Questions', icon: HelpCircle },
    { id: 'molecular-hydrogen', name: 'Molecular Hydrogen', icon: Atom },
    { id: 'skincare', name: 'Skincare', icon: Sparkles },
    { id: 'infrared-therapy', name: 'Infrared Therapy', icon: Sun },
  ];

  const faqs = [
    {
      category: 'molecular-hydrogen',
      categoryLabel: 'Molecular Hydrogen',
      questions: [
        {
          q: 'What is the difference between HW600 and HW300?',
          a: `The main difference is hydrogen output:

• HW300 – Delivers 300 mL/min of hydrogen gas. Compact and portable, it's designed for convenient personal or home use.
• HW600 – Produces 600 mL/min of hydrogen gas, offering double the output. This allows for shorter sessions or more frequent use, and is often preferred by those who want higher-capacity performance.

Both models include accessories for multiple types of hydrogen use: inhalation, ear application (with headphones), eye application (with goggles), and water infusion. The HW600 also includes a digital hydrogen meter for monitoring hydrogen concentration levels.`
        },
        {
          q: 'Can you achieve the same results with the HW300 as the HW600?',
          a: `Yes, both models deliver the same type and purity of hydrogen gas. The difference is simply the output volume:

• HW300: 300 mL/min
• HW600: 600 mL/min

This means the HW600 produces hydrogen twice as fast. To receive a similar total volume of hydrogen, a session on the HW300 would need to be about twice as long as one on the HW600. So yes — you can achieve a comparable experience with either device; the HW600 is just more time-efficient.`
        },
        {
          q: 'How long should I use the hydrogen therapy machine each day?',
          a: `For new users:
• Start gradually with 15–30 minutes per session to get comfortable
• Focus on relaxed, steady breathing
• Over 1–2 weeks, you can increase to 1–2 hours daily, based on preference
• Some users prefer using the device several times a week instead of daily

Your routine can be flexible—there's no single "right" way. The best approach is to experiment and see what fits your lifestyle.`
        },
        {
          q: 'Should I feel air coming out? It doesn\'t feel like I\'m breathing anything.',
          a: `Yes, this is completely normal. The HW300 and HW600 are designed to deliver pure hydrogen gas gently and subtly, which means you may not feel a strong airflow. Unlike oxygen, hydrogen is a much lighter gas, so it diffuses differently and is not always perceptible during inhalation.

For those who prefer a stronger flow sensation, commercial units provide a more noticeable hydrogen output.`
        },
        {
          q: 'Is there any guarantee of results with H2 systems?',
          a: `There is no guarantee of specific health results with any H2 system. Every body is different, and responses to hydrogen therapy can vary based on many factors like lifestyle, existing conditions, and overall health.

That said, we've seen incredible feedback from customers around the world—but we always recommend trying the product consistently and seeing how your body responds.`
        },
        {
          q: 'Will tablets give me the same experience as a machine?',
          a: `Both options deliver molecular hydrogen, but in different ways:

• Machines – Provide a steady stream of hydrogen gas for inhalation or water infusion, making them practical for longer or repeated use.
• Supersaturated Hydrogen (SSH) Tablets – Taken orally, where they release hydrogen in the stomach. Some people find this convenient when traveling or looking for a quick option.

Machines are often preferred by those who want unlimited access without restocking, while tablets are portable and simple. Many of our customers like using both, depending on the situation.`
        },
        {
          q: 'How much maintenance do the hydrogen machines require?',
          a: `Our machines are designed for minimal maintenance, but regular care ensures optimal performance and longevity.

• Water Quality: Always use pure distilled water to prevent mineral buildup and extend the lifespan of the machine.
• Cannulas & Accessories: Should be disinfected regularly and given a deep clean every few months to maintain hygiene.
• Infusion Rod (for Water Infusion): If using mineralized water, occasional cleaning may be needed to prevent clogging.
• HW300 & HW600: These models require very little upkeep beyond keeping the water level between minimum and maximum marks.`
        },
        {
          q: 'Is the machine suitable for use on 230V circuits (UK/EU)?',
          a: `Yes, our machines include a converter, making them compatible with all power outlets, including 230V circuits in the UK and EU. Additionally, you will receive a power cord appropriate for the country you live in.`
        }
      ]
    },
    {
      category: 'skincare',
      categoryLabel: 'Skincare',
      questions: [
        {
          q: 'What makes Beauté Pacifique different from other skincare brands?',
          a: `Beauté Pacifique is a Danish cosmeceutical brand that was the first in the world to document significant skin rejuvenation through objective ultrasound scanning. Their products deliver near-medical efficacy with scientifically proven results.

The brand uses advanced delivery systems that ensure active ingredients penetrate deeply into the skin where they can be most effective, rather than sitting on the surface.`
        },
        {
          q: 'How long does it take to see results with Beauté Pacifique?',
          a: `Results vary by individual and product, but many users report visible improvements in skin texture and appearance within 4-8 weeks of consistent use. The brand supports documentation through ultrasound scanning for objective measurement of skin improvement.`
        },
        {
          q: 'Are Beauté Pacifique products suitable for sensitive skin?',
          a: `Beauté Pacifique offers products for various skin types, including sensitive skin. We recommend consulting with our team to find the most suitable products for your specific skin concerns and sensitivities. The brand's scientific approach means products are formulated to minimize irritation while maximizing efficacy.`
        },
        {
          q: 'What is the Masculinity range?',
          a: `The Masculinity range is specifically designed for men's skincare needs. Men's skin is typically thicker and produces more sebum than women's skin, requiring different formulations. This range addresses common male skin concerns including shaving irritation, rough texture, and signs of aging.`
        }
      ]
    },
    {
      category: 'infrared-therapy',
      categoryLabel: 'Infrared Therapy',
      questions: [
        {
          q: 'What is full spectrum infrared energy?',
          a: `Full spectrum infrared energy is the healthiest part of the solar electromagnetic spectrum. The infrared frequencies are invisible to the naked eye, but are felt within the body as deep penetrating heat and warmth.

Full Spectrum Infrared energy includes three wavelengths:
• Near (NIR) – Effective for skin health and cellular energy
• Mid (MIR) – Penetrates deeper into soft tissue
• Far (FIR) – Provides the deepest penetration for detoxification

NASA scientists have determined their differing wavelengths and health studies have shown what these different wavelengths do, which is why Therasage devices generate the Full Infrared Spectrum to deliver all these health benefits in one sauna.`
        },
        {
          q: 'What is the difference between red light and infrared?',
          a: `Red light is visible and is most effective for use on the surface of the skin. Red light occupies the end of the visible spectrum with wavelengths between 600nm-700nm.

Infrared is invisible, and as you increase the wavelength from red to infrared, you also increase the penetration depth. Each part of the sun's spectrum offers different health benefits. Infrared penetrates deeper into tissues while red light works primarily on surface-level skin concerns.`
        },
        {
          q: 'What are some benefits of full spectrum infrared heat?',
          a: `Full spectrum infrared has the combined benefits of all wavelengths working together synergistically. Key benefits include:

• Improved circulation & oxygenation
• Pain relief
• Healthy weight loss support
• Total body detoxification
• Immune enhancement
• Decreased inflammation
• Improved sleep
• Stress reduction
• Increased metabolism
• Anti-aging effects

It is the synergistic effects of these wavelengths working together that makes natural healing so powerful and effective.`
        },
        {
          q: 'Why is a sauna where your head stays outside better?',
          a: `The body below the neck can withstand more heat than the brain. The most effective way to initiate a natural immune response is by raising the core temperature—this is the body's front line defense against foreign viruses and bacteria.

At higher than normal temperatures, bacteria, parasites, and viruses begin to die off. However, at higher temperatures, the brain can be negatively affected. Most health professionals recommend using a format that protects your brain from overheating while still allowing you to raise your core body temperature and promote organ and tissue detoxification.`
        },
        {
          q: 'What is EMF and should I be concerned about it?',
          a: `Electromagnetic frequency (EMF) is energy discharged by all alternating current (AC) electrical devices, wireless routers, Bluetooth devices, mobile phones, smart meters, and many home appliances.

The World Health Organization (WHO) has declared that high levels of EMF can be harmful for health. Therasage products are designed with EMF protection features to minimize exposure during use.`
        },
        {
          q: 'What causes pain and how does infrared help?',
          a: `Pain is one of the body's main signals that something is wrong. Often, pain is caused by inflammation, which is also tied to high levels of toxicity. Acute pain from injury can be accompanied by swelling that adds pressure on pain centers.

Therasage infrared saunas can improve this condition by emitting deep penetrating healthy heat that soothes injured areas and decreases inflammation. The sauna also improves circulation, decreases swelling, and improves oxygenation to bring more healing elements to the body.`
        },
        {
          q: 'Is an infrared sauna safe with breast implants or metal implants?',
          a: `Yes, most infrared saunas are safe to use with breast implants or augmentation. For metal implants, those made within the last 20-25 years are typically titanium and are safe to expose to infrared.

Metal pins, rods, artificial joints, or other implants made of metals and surgical steel generally reflect infrared waves, making infrared sauna use perfectly safe.`
        },
        {
          q: 'Is the sauna safe for children?',
          a: `Many children benefit greatly from infrared sauna use with specific advice and protocols. We recommend contacting us for special instructions and protocols tailored to children's needs. Adult supervision is always required.`
        }
      ]
    }
  ];

  // Filter FAQs based on category and search
  const filteredFaqs = faqs
    .filter(category => activeCategory === 'all' || category.category === activeCategory)
    .map(category => ({
      ...category,
      questions: category.questions.filter(
        q => 
          searchQuery === '' ||
          q.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
          q.a.toLowerCase().includes(searchQuery.toLowerCase())
      )
    }))
    .filter(category => category.questions.length > 0);

  const totalQuestions = filteredFaqs.reduce((acc, cat) => acc + cat.questions.length, 0);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero */}
      <section className="relative bg-[#0D2847] text-white py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#0D2847] via-[#164175] to-[#0D2847] opacity-50"></div>
        <div className="absolute top-0 left-0 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Frequently Asked Questions</h1>
          <p className="text-lg text-white/80 max-w-2xl mx-auto mb-8">
            Find answers to common questions about our wellness solutions from Axiom H2, Beauté Pacifique, and Therasage
          </p>
          
          {/* Search Bar */}
          <div className="max-w-xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Search questions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 pr-4 py-3 w-full rounded-xl bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:bg-white/20 focus:border-white/40"
              data-testid="faq-search-input"
            />
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="sticky top-0 bg-white/95 backdrop-blur-sm border-b border-gray-100 z-20 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((cat) => {
              const IconComponent = cat.icon;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  data-testid={`faq-category-${cat.id}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    activeCategory === cat.id
                      ? 'bg-[#0D2847] text-white shadow-lg'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <IconComponent className="w-4 h-4" />
                  {cat.name}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Results count */}
          <p className="text-sm text-gray-500 mb-6 text-center">
            Showing {totalQuestions} question{totalQuestions !== 1 ? 's' : ''}
            {searchQuery && ` for "${searchQuery}"`}
          </p>

          {filteredFaqs.length === 0 ? (
            <Card className="p-12 text-center">
              <HelpCircle className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No questions found</h3>
              <p className="text-gray-500">Try adjusting your search or category filter</p>
            </Card>
          ) : (
            filteredFaqs.map((category, catIndex) => (
              <div key={catIndex} className="mb-10" data-testid={`faq-section-${category.category}`}>
                <div className="flex items-center gap-3 mb-6">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    category.category === 'molecular-hydrogen' ? 'bg-cyan-100 text-cyan-600' :
                    category.category === 'skincare' ? 'bg-pink-100 text-pink-600' :
                    'bg-orange-100 text-orange-600'
                  }`}>
                    {category.category === 'molecular-hydrogen' && <Atom className="w-5 h-5" />}
                    {category.category === 'skincare' && <Sparkles className="w-5 h-5" />}
                    {category.category === 'infrared-therapy' && <Sun className="w-5 h-5" />}
                  </div>
                  <h2 className="text-2xl font-bold text-[#0D2847]">{category.categoryLabel}</h2>
                </div>
                
                <div className="space-y-3">
                  {category.questions.map((faq, qIndex) => {
                    const globalIndex = `${catIndex}-${qIndex}`;
                    const isOpen = openIndex === globalIndex;
                    
                    return (
                      <Card 
                        key={qIndex} 
                        className={`overflow-hidden transition-all duration-200 ${
                          isOpen ? 'ring-2 ring-[#0D2847]/20 shadow-lg' : 'hover:shadow-md'
                        }`}
                        data-testid={`faq-item-${catIndex}-${qIndex}`}
                      >
                        <button
                          onClick={() => setOpenIndex(isOpen ? null : globalIndex)}
                          className="w-full p-5 text-left flex justify-between items-start gap-4 hover:bg-gray-50/50 transition-colors"
                          data-testid={`faq-toggle-${catIndex}-${qIndex}`}
                        >
                          <span className="font-semibold text-[#0D2847] leading-relaxed">{faq.q}</span>
                          <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                            isOpen ? 'bg-[#0D2847] text-white' : 'bg-gray-100 text-gray-500'
                          }`}>
                            {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                          </div>
                        </button>
                        {isOpen && (
                          <div className="px-5 pb-5 animate-in slide-in-from-top-2 duration-200">
                            <div className="border-t border-gray-100 pt-4">
                              <p className="text-gray-600 leading-relaxed whitespace-pre-line">{faq.a}</p>
                            </div>
                          </div>
                        )}
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))
          )}

          {/* Contact CTA */}
          <Card className="bg-gradient-to-br from-[#0D2847] to-[#164175] border-none mt-12 overflow-hidden relative">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2"></div>
            <div className="p-8 text-center relative z-10">
              <h3 className="text-2xl font-bold text-white mb-4">Still Have Questions?</h3>
              <p className="text-white/80 mb-6 max-w-md mx-auto">
                Our wellness experts are here to help you find the perfect solution for your health journey.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="/contact" 
                  className="inline-block bg-white text-[#0D2847] px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
                  data-testid="faq-contact-btn"
                >
                  Contact Us
                </a>
                <a 
                  href="https://wa.me/971567295491" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-block bg-green-500 text-white px-8 py-3 rounded-lg font-semibold hover:bg-green-600 transition-colors"
                  data-testid="faq-whatsapp-btn"
                >
                  WhatsApp Us
                </a>
              </div>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
